import {
  ComponentRef,
  EventEmitter,
  Injectable,
  Output,
  Type,
  ViewContainerRef
} from '@angular/core';
import {ModalWindowsTypes} from 'src/app/core/enums';
import {IModalWindow} from 'src/app/utils/interfaces/modal-dialog.interface';

@Injectable({
  providedIn: 'root'
})
export class ModalWindowService {
  container: ViewContainerRef | null = null;
  componentRef: ComponentRef<IModalWindow> | null = null;
  @Output() openModal: EventEmitter<{
    open: boolean,
    customClass: string | null,
    windowType: ModalWindowsTypes
  }> = new EventEmitter();
  @Output() closingModal: EventEmitter<boolean> = new EventEmitter();
  @Output() startUpload: EventEmitter<string> = new EventEmitter();
  @Output() reopeningDlg: EventEmitter<any> = new EventEmitter();
  @Output() saveAsOk: EventEmitter<boolean> = new EventEmitter();
  @Output() deleteFileFired: EventEmitter<boolean> = new EventEmitter();
  @Output() deleteCancelled: EventEmitter<boolean> = new EventEmitter();
  @Output() showCross: EventEmitter<boolean> = new EventEmitter();
  @Output() allowedClickingOutside: EventEmitter<boolean> = new EventEmitter();
  @Output() nameRetrieved: EventEmitter<string> = new EventEmitter();
  @Output() doNotShowAgainOk: EventEmitter<void> = new EventEmitter();

  get isModalWindowOpened(): boolean { return this.componentRef !== null; }

  constructor() {
  }

  createModalWindow(type: Type<IModalWindow>, options?: any): void {
    this.componentFactory(type, options);
  }

  componentFactory(type: Type<IModalWindow>, options?: any) {
    this.removeElemetRef();

    if (this.container) {
      this.componentRef = this.container.createComponent<IModalWindow>(type);
      switch (this.componentRef.instance.getWindowEnumType()) {
        case ModalWindowsTypes.EXTRA_COPY_DIALOG:
          (this.componentRef.instance as any).ok = this.okSaveAs;
          (this.componentRef.instance as any).no = this.closeAndReopenCloudDlg;
          break;
        case ModalWindowsTypes.COMMON_ERROR_DIALOG:
          (this.componentRef.instance as any).doNotShowAgainOk = this.okDoNotShowAgain;
          (this.componentRef.instance as any).ok = this.noClose;
          break;
        case ModalWindowsTypes.SPINNER_DIALOG:
          this.showCross.emit(false);
          break;
        case ModalWindowsTypes.GET_FOLDER_NAME:
          (this.componentRef.instance as any).ok = this.okName;
          (this.componentRef.instance as any).no = this.noClose;
          break;
        case ModalWindowsTypes.DELETE_FILE_DIALOG:
          (this.componentRef.instance as any).ok = this.okDelete;
          (this.componentRef.instance as any).no = this.closeAndReopenCloudDlg;
          break;

        case ModalWindowsTypes.AUTOSAVE_RESTORE_DIALOG:
          (this.componentRef.instance as any).unsavedFiles = options;
          (this.componentRef.instance as any).ok = this.okClose;
          break;
        case ModalWindowsTypes.AUDIOMAKER_DIALOG:
          (this.componentRef.instance as any).ok = this.okClose;
          (this.componentRef.instance as any).no = this.noClose;
          break;
        case ModalWindowsTypes.EXAM_MODE_PROPERTIES_WINDOW:
          (this.componentRef.instance as any).ok = this.okClose;
          (this.componentRef.instance as any).no = this.noClose;
          this.allowedClickingOutside.emit(false);
          break;
        default:
          (this.componentRef.instance as any).ok = this.okClose;
          (this.componentRef.instance as any).no = this.noClose;
          break;
      }

      const setOptionsInstance = this.componentRef.instance.getInstanceOf('ISetOptions');
      if (setOptionsInstance && options) {
        setOptionsInstance.setOptions(options);
      }

      let customClass: null | string = null;
      const getClassInstance = this.componentRef.instance.getInstanceOf('IGetClass');
      if (getClassInstance) {
        customClass = getClassInstance.getClass() as string;
      }

      this.openModal.emit({
        open: true,
        customClass,
        windowType: this.componentRef.instance.getWindowEnumType()
      });
    }
  }

  closeModal = () => {
    if (this.componentRef) {
      const windowType = this.componentRef.instance.getWindowEnumType();
      this.removeElemetRef();
      this.openModal.emit({
        open: false,
        customClass: null,
        windowType
      });
    }
  }

  closeByCrossModal = () => {
    if (this.componentRef) {
      this.closeModal();
      this.closingModal.emit();
    }
  }

  closeSpinner = () => {
    if (this.componentRef === null) {
      return;
    }

    const windowType = this.componentRef.instance.getWindowEnumType();
    if (this.componentRef.instance.getWindowEnumType() === ModalWindowsTypes.SPINNER_DIALOG) {
      this.removeElemetRef();
      this.openModal.emit({
        open: false,
        customClass: null,
        windowType
      });
    }
  }

  noClose = () => {
    if (this.componentRef) {
      this.closeModal();
      this.closingModal.emit(false);
    }
  }

  okClose = () => {
    if (this.componentRef) {
      this.closeModal();
      this.closingModal.emit(true);
    }
  }

  okName = (value) => {
    if (this.componentRef) {
      this.removeElemetRef();
      this.nameRetrieved.emit(value);
    }
  }

  okDelete = () => {
    if (this.componentRef) {
      // this.removeElemetRef();
      this.closeModal();
      this.deleteFileFired.emit();
    }
  }

  closeAndReopenCloudDlg = () => {
    if (this.componentRef) {
      this.closeModal();
      this.reopeningDlg.emit();
    }
  }

  okSaveAs = () => {
    if (this.componentRef) {
      this.closeModal();
      this.saveAsOk.emit(true);
    }
  }

  okDoNotShowAgain = () => {
    if (this.componentRef) {
      this.closeModal();
      this.doNotShowAgainOk.emit();
    }
  }

  setModalContainer(container: ViewContainerRef): void {
    this.container = container;
  }

  cleanOndestroy() {
    this.removeElemetRef();
  }

  disable(): void {
    this.allowedClickingOutside.emit(false);
    this.showCross.emit(false);
  }

  enable(): void {
    this.allowedClickingOutside.emit(true);
    this.showCross.emit(true);
  }

  private removeElemetRef() {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
